import { apiFetch } from '../../../../../shared/services/apiClient';
import { EstadoBolsa } from '../state/bolsaMachine';
import {
  ApiErrorBody,
  BolsaCentro,
  BolsaConReprecio,
  CentroApiError,
  DetalleCentro,
  ListarCentroResultado,
  MetricasCentro,
} from '../types';

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/cotizaciones`;

async function handle<T>(res: Response): Promise<T> {
  const body = await res.json();
  if (!res.ok) {
    const errBody = body as ApiErrorBody;
    throw new CentroApiError(errBody.error);
  }
  return body.data as T;
}

// SPEC-100 (RN-002) — desde/hasta opcionales, formato YYYY-MM-DD, filtran por bolsas.created_at
export interface RangoFechasApi {
  desde?: string;
  hasta?: string;
}

// SPEC-206 (design §7.2) — la versión enviada con su estado, validez y PDF.
export interface ResultadoEnvio {
  version: { version: number; estado: string; validez: string | null; enviada_en: string | null; pdf_disponible: boolean };
  estado_envio: string;
  destinatarios: Array<{ contacto_id: string | null; correo: string; estado: string; error?: string }>;
}

export const centroApi = {
  async listarBolsasAbiertas(rango: RangoFechasApi = {}): Promise<BolsaCentro[]> {
    const params = new URLSearchParams();
    params.set('estado', EstadoBolsa.ACTIVA);
    if (rango.desde) params.set('desde', rango.desde);
    if (rango.hasta) params.set('hasta', rango.hasta);
    const res = await apiFetch(`${API_BASE}/bolsas?${params.toString()}`);
    return handle<BolsaCentro[]>(res);
  },

  async listarCentro(
    filtros: { estado?: string; q?: string; page?: number; limit?: number } & RangoFechasApi = {},
  ): Promise<ListarCentroResultado> {
    const params = new URLSearchParams();
    if (filtros.estado) params.set('estado', filtros.estado);
    if (filtros.q) params.set('q', filtros.q);
    if (filtros.page) params.set('page', String(filtros.page));
    if (filtros.limit) params.set('limit', String(filtros.limit));
    if (filtros.desde) params.set('desde', filtros.desde);
    if (filtros.hasta) params.set('hasta', filtros.hasta);
    const res = await apiFetch(`${API_BASE}/centro?${params.toString()}`);
    const body = await res.json();
    if (!res.ok) {
      throw new CentroApiError((body as ApiErrorBody).error);
    }
    return { items: body.data, total: body.meta.total, page: body.meta.page, limit: body.meta.limit };
  },

  async obtenerDetalle(id: string): Promise<DetalleCentro> {
    const res = await apiFetch(`${API_BASE}/centro/${id}`);
    return handle<DetalleCentro>(res);
  },

  async obtenerMetricas(rango: RangoFechasApi = {}): Promise<MetricasCentro> {
    const params = new URLSearchParams();
    if (rango.desde) params.set('desde', rango.desde);
    if (rango.hasta) params.set('hasta', rango.hasta);
    const query = params.toString();
    const res = await apiFetch(`${API_BASE}/centro/metricas${query ? `?${query}` : ''}`);
    return handle<MetricasCentro>(res);
  },

  async convertirBolsa(id: string): Promise<BolsaCentro> {
    const res = await apiFetch(`${API_BASE}/bolsas/${id}/convertir`, { method: 'POST' });
    return handle<BolsaCentro>(res);
  },

  async enviarCotizacion(
    id: string,
    payload: {
      destinatarios: Array<{ contacto_id: string; correo: string; nombre: string }>;
      subject: string;
      validez?: string;
    },
  ): Promise<ResultadoEnvio> {
    const res = await apiFetch(`${API_BASE}/bolsas/${id}/enviar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handle<ResultadoEnvio>(res);
  },

  // SPEC-206 (design §7.1) — ENVIADA|VENCIDA → BORRADOR devuelve además el resumen del re-precio.
  async transicionar(id: string, to: EstadoBolsa, reason?: string): Promise<BolsaConReprecio> {
    const res = await apiFetch(`${API_BASE}/bolsas/${id}/transiciones`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, reason }),
    });
    return handle<BolsaConReprecio>(res);
  },

  // SPEC-206 (UC-009, design §7.5) — copia en BORRADOR re-preciada; cliente opcional.
  async duplicar(id: string, clienteId?: string | null): Promise<BolsaConReprecio> {
    const res = await apiFetch(`${API_BASE}/bolsas/${id}/duplicar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(clienteId ? { cliente_id: clienteId } : {}),
    });
    return handle<BolsaConReprecio>(res);
  },

  // SPEC-206 (UC-007, design §7.4) — el PDF exacto que recibió el cliente en esa versión.
  async descargarPdfVersion(id: string, version: number): Promise<Blob> {
    const res = await apiFetch(`${API_BASE}/centro/${id}/versiones/${version}/pdf`);
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
      throw new CentroApiError(body?.error ?? { code: 'ERROR', message: 'No se pudo descargar el PDF' });
    }
    return res.blob();
  },

  // SPEC-103 UC-002 — asocia un cliente existente a una bolsa sin cliente
  async asociarCliente(id: string, clienteId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/bolsas/${id}/cliente`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clienteId }),
    });
    await handle<{ id: string; cliente_id: string }>(res);
  },
};
